import { useRef, useState } from "react";
import intl from "react-intl-universal";
import { TbChevronDown, TbChevronRight, TbPhoto, TbTrash } from "react-icons/tb";
import { getCardNetworkLabel } from "../../../../../Components/Atoms/CardNetworkLogo";
import { CardMastercardHologramLogo } from "./CardMastercardHologramLogo";
import { useToast } from "../../../../../Hooks/toast";
import {
  getMastercardVariantConfig,
  MASTERCARD_VARIANTS,
  DEFAULT_CARD_BACK_PHONE_METROPOLITAN,
  DEFAULT_CARD_BACK_PHONE_OTHER,
  DEFAULT_CARD_BACK_WEBSITE,
  DEFAULT_CARD_NUMBER_PREVIEW,
  DEFAULT_CARDHOLDER_PREVIEW_NAME,
  DEFAULT_CVC_PREVIEW,
  DEFAULT_EXPIRY_PREVIEW,
  validateCardLogoFile,
} from "../cardProgramDesignConstants";
import { CardDesignPreview } from "./CardDesignPreview";

const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

const Field = ({ label, children, hint }) => (
  <label className="block text-sm">
    <span className="mb-1 block text-gray-600 dark:text-gray-400">{label}</span>
    {children}
    {hint ? <span className="mt-1 block text-xs text-gray-400 dark:text-gray-500">{hint}</span> : null}
  </label>
);

const CollapsibleSection = ({ title, description, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-xl border border-gray-100 dark:border-brand-dark-border">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        <div>
          <p className="dashboard-section-label">{title}</p>
          {description ? (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{description}</p>
          ) : null}
        </div>
        {open ? (
          <TbChevronDown size={18} className="shrink-0 text-gray-400" aria-hidden />
        ) : (
          <TbChevronRight size={18} className="shrink-0 text-gray-400" aria-hidden />
        )}
      </button>
      {open ? <div className="space-y-4 border-t border-gray-100 px-4 py-4 dark:border-brand-dark-border">{children}</div> : null}
    </div>
  );
};

const MastercardVariantOption = ({ variant, selected, onSelect, disabled = false }) => (
  <button
    type="button"
    onClick={() => {
      if (!disabled) onSelect(variant.id);
    }}
    disabled={disabled}
    className={`box-border flex h-[4.75rem] min-w-0 flex-1 flex-col justify-center rounded-lg border px-3 py-2.5 text-left transition disabled:cursor-not-allowed disabled:opacity-60 ${
      selected
        ? "border-[#e7a73f] bg-[#e7a73f]/5 text-brand-dark dark:text-gray-100"
        : `border-gray-200 text-gray-600 dark:border-brand-dark-border dark:text-gray-400${
            disabled ? "" : " hover:border-gray-300 dark:hover:border-gray-500"
          }`
    }`}
  >
    <span className="block h-4 truncate text-xs font-semibold capitalize leading-4">
      {intl.get(`baas.cartoes.design.mastercardVariant.${variant.id}`)}
    </span>
    <span className="mt-1 block h-4 overflow-hidden text-ellipsis whitespace-nowrap text-[11px] leading-4 text-gray-500 dark:text-gray-400">
      {intl.get(`baas.cartoes.design.productType.${variant.productType}`)}
    </span>
  </button>
);

const ImageUploadField = ({
  label,
  hint,
  value,
  onUpload,
  onRemove,
  accept = "image/png,image/svg+xml,image/jpeg,image/jpg,image/webp",
  uploadLabel,
  removeLabel,
  disabled = false,
}) => {
  const fileInputRef = useRef(null);

  return (
    <Field label={label} hint={hint}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={onUpload}
        disabled={disabled}
      />
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn-outline inline-flex !h-9 items-center gap-2 !px-3 !text-xs disabled:cursor-not-allowed disabled:opacity-60"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
        >
          <TbPhoto className="h-4 w-4" />
          {uploadLabel}
        </button>
        {value ? (
          <button
            type="button"
            className="btn-outline inline-flex !h-9 items-center gap-2 !px-3 !text-xs text-red-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-red-400"
            onClick={onRemove}
            disabled={disabled}
          >
            <TbTrash className="h-4 w-4" />
            {removeLabel}
          </button>
        ) : null}
      </div>
    </Field>
  );
};

export const BaasCardProgramDesignForm = ({
  design,
  onChange,
  showProgramName = true,
  nameAndLogoLocked = false,
}) => {
  const { addToast } = useToast();
  const [previewSide, setPreviewSide] = useState("front");

  const updateDesign = (patch) => {
    if (nameAndLogoLocked) {
      const next = { ...patch };
      delete next.name;
      delete next.customLogoUrl;
      delete next.mastercardVariant;
      delete next.productType;
      delete next.backgroundColor;
      delete next.website;
      delete next.phoneMetropolitan;
      delete next.phoneOther;
      delete next.cardNumberPreview;
      delete next.expiryPreview;
      delete next.cvcPreview;
      delete next.cardholderPreviewName;
      if (Object.keys(next).length === 0) return;
      onChange({ ...design, ...next });
      return;
    }
    onChange({ ...design, ...patch });
  };

  const applyMastercardVariant = (variantId) => {
    const variant = getMastercardVariantConfig(variantId);
    updateDesign({
      mastercardVariant: variant.id,
      productType: variant.productType,
      backgroundColor: variant.backgroundColor,
    });
  };

  const readImageFile = (file, { validate, onSuccess }) => {
    const validation = validate(file);
    if (!validation.valid) {
      addToast({
        type: "error",
        title: intl.get(`baas.cartoes.design.logoUploadError.${validation.reason}`),
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      onSuccess(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    readImageFile(file, {
      validate: validateCardLogoFile,
      onSuccess: (dataUrl) => updateDesign({ customLogoUrl: dataUrl }),
    });
  };

  const mastercardVariant = design.mastercardVariant ?? "black";

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-start">
      <div className="min-w-0 space-y-5 rounded-xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface">
        {nameAndLogoLocked ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
            {intl.get("baas.cartoes.design.nameAndLogoLocked")}
          </p>
        ) : null}

        {showProgramName ? (
          <Field
            label={intl.get("baas.cartoes.design.programName")}
            hint={
              nameAndLogoLocked
                ? intl.get("baas.cartoes.design.nameAndLogoLockedHint")
                : undefined
            }
          >
            <input
              type="text"
              className={`${inputClass} disabled:cursor-not-allowed disabled:opacity-60`}
              value={design.name ?? ""}
              onChange={(event) => updateDesign({ name: event.target.value })}
              placeholder={intl.get("baas.cartoes.design.programNamePlaceholder")}
              disabled={nameAndLogoLocked}
              readOnly={nameAndLogoLocked}
            />
          </Field>
        ) : null}

        <ImageUploadField
          label={intl.get("baas.cartoes.design.logoUpload")}
          hint={
            nameAndLogoLocked
              ? intl.get("baas.cartoes.design.nameAndLogoLockedHint")
              : intl.get("baas.cartoes.design.logoUploadHint")
          }
          value={design.customLogoUrl}
          onUpload={handleLogoUpload}
          onRemove={() => updateDesign({ customLogoUrl: null })}
          uploadLabel={intl.get("baas.cartoes.design.logoUploadButton")}
          removeLabel={intl.get("baas.cartoes.design.logoRemove")}
          disabled={nameAndLogoLocked}
        />

        <Field label={intl.get("baas.cartoes.design.network")}>
          <div className="flex w-full items-center justify-center gap-2 rounded-lg border border-[#e7a73f] bg-[#e7a73f]/5 px-3 py-2.5 text-xs font-medium text-brand-dark dark:text-gray-100">
            <CardMastercardHologramLogo variant="brand" width={36} />
            <span>{getCardNetworkLabel("mastercard")}</span>
          </div>
        </Field>

        <Field
          label={intl.get("baas.cartoes.design.mastercardVariant.label")}
          hint={
            nameAndLogoLocked
              ? intl.get("baas.cartoes.design.nameAndLogoLockedHint")
              : undefined
          }
        >
          <div className="flex gap-2">
            {Object.values(MASTERCARD_VARIANTS).map((variant) => (
              <MastercardVariantOption
                key={variant.id}
                variant={variant}
                selected={mastercardVariant === variant.id}
                onSelect={applyMastercardVariant}
                disabled={nameAndLogoLocked}
              />
            ))}
          </div>
        </Field>

        <CollapsibleSection
          title={intl.get("baas.cartoes.design.back.sectionTitle")}
          description={
            nameAndLogoLocked
              ? intl.get("baas.cartoes.design.nameAndLogoLockedHint")
              : intl.get("baas.cartoes.design.back.sectionDescription")
          }
          defaultOpen={previewSide === "back"}
        >
          <Field label={intl.get("baas.cartoes.design.back.website")}>
            <input
              type="text"
              className={`${inputClass} disabled:cursor-not-allowed disabled:opacity-60`}
              value={design.website ?? DEFAULT_CARD_BACK_WEBSITE}
              onChange={(event) => updateDesign({ website: event.target.value })}
              placeholder={DEFAULT_CARD_BACK_WEBSITE}
              disabled={nameAndLogoLocked}
              readOnly={nameAndLogoLocked}
            />
          </Field>

          <Field label={intl.get("baas.cartoes.design.back.phoneMetropolitan")}>
            <input
              type="text"
              className={`${inputClass} disabled:cursor-not-allowed disabled:opacity-60`}
              value={design.phoneMetropolitan ?? DEFAULT_CARD_BACK_PHONE_METROPOLITAN}
              onChange={(event) => updateDesign({ phoneMetropolitan: event.target.value })}
              placeholder={DEFAULT_CARD_BACK_PHONE_METROPOLITAN}
              disabled={nameAndLogoLocked}
              readOnly={nameAndLogoLocked}
            />
          </Field>

          <Field label={intl.get("baas.cartoes.design.back.phoneOther")}>
            <input
              type="text"
              className={`${inputClass} disabled:cursor-not-allowed disabled:opacity-60`}
              value={design.phoneOther ?? DEFAULT_CARD_BACK_PHONE_OTHER}
              onChange={(event) => updateDesign({ phoneOther: event.target.value })}
              placeholder={DEFAULT_CARD_BACK_PHONE_OTHER}
              disabled={nameAndLogoLocked}
              readOnly={nameAndLogoLocked}
            />
          </Field>

          <Field label={intl.get("baas.cartoes.design.back.cardNumberPreview")}>
            <input
              type="text"
              className={`${inputClass} disabled:cursor-not-allowed disabled:opacity-60`}
              value={design.cardNumberPreview ?? DEFAULT_CARD_NUMBER_PREVIEW}
              onChange={(event) => updateDesign({ cardNumberPreview: event.target.value })}
              placeholder={DEFAULT_CARD_NUMBER_PREVIEW}
              disabled={nameAndLogoLocked}
              readOnly={nameAndLogoLocked}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={intl.get("baas.cartoes.design.back.expiryPreview")}>
              <input
                type="text"
                className={`${inputClass} disabled:cursor-not-allowed disabled:opacity-60`}
                value={design.expiryPreview ?? DEFAULT_EXPIRY_PREVIEW}
                onChange={(event) => updateDesign({ expiryPreview: event.target.value })}
                placeholder={DEFAULT_EXPIRY_PREVIEW}
                disabled={nameAndLogoLocked}
                readOnly={nameAndLogoLocked}
              />
            </Field>

            <Field label={intl.get("baas.cartoes.design.back.cvcPreview")}>
              <input
                type="text"
                className={`${inputClass} disabled:cursor-not-allowed disabled:opacity-60`}
                value={design.cvcPreview ?? DEFAULT_CVC_PREVIEW}
                onChange={(event) => updateDesign({ cvcPreview: event.target.value })}
                placeholder={DEFAULT_CVC_PREVIEW}
                disabled={nameAndLogoLocked}
                readOnly={nameAndLogoLocked}
              />
            </Field>
          </div>

          <Field label={intl.get("baas.cartoes.design.back.cardholderName")}>
            <input
              type="text"
              className={`${inputClass} disabled:cursor-not-allowed disabled:opacity-60`}
              value={design.cardholderPreviewName ?? DEFAULT_CARDHOLDER_PREVIEW_NAME}
              onChange={(event) => updateDesign({ cardholderPreviewName: event.target.value })}
              placeholder={DEFAULT_CARDHOLDER_PREVIEW_NAME}
              disabled={nameAndLogoLocked}
              readOnly={nameAndLogoLocked}
            />
          </Field>
        </CollapsibleSection>
      </div>

      <div className="flex w-fit flex-col gap-2 lg:sticky lg:top-4 lg:pl-2">
        <p className="dashboard-section-label mb-3">
          {intl.get("baas.cartoes.design.previewTitle")}
        </p>
        <div
          className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 dark:border-brand-dark-border dark:bg-brand-dark-elevated"
          role="tablist"
          aria-label={intl.get("baas.cartoes.design.previewTitle")}
        >
          {["front", "back"].map((side) => {
            const selected = previewSide === side;

            return (
              <button
                key={side}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setPreviewSide(side)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                  selected
                    ? "bg-white text-brand-dark shadow-sm dark:bg-brand-dark-surface dark:text-gray-100"
                    : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                }`}
              >
                {intl.get(`baas.cartoes.design.preview${side === "front" ? "Front" : "Back"}`)}
              </button>
            );
          })}
        </div>
        <CardDesignPreview
          backgroundColor={design.backgroundColor}
          cardholderName={design.cardholderPreviewName}
          customLogo={design.customLogoUrl}
          mastercardVariant={design.mastercardVariant}
          network={design.network ?? "mastercard"}
          side={previewSide}
          website={design.website}
          phoneMetropolitan={design.phoneMetropolitan}
          phoneOther={design.phoneOther}
          cardNumberPreview={design.cardNumberPreview}
          expiryPreview={design.expiryPreview}
          cvcPreview={design.cvcPreview}
        />
        <p className="text-xs text-gray-400 dark:text-gray-500">
          {intl.get("baas.cartoes.design.previewStageHint")}
        </p>
      </div>
    </div>
  );
};
